import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IDexBigTrade, ITicker } from '@kattana-io/common';
import Text from '../../../atoms/Text';
import {
  IMobileTableScema,
  formatMobileTableData,
} from '../../../helpers/formatters';
import PairIcons from '../../../molecules/PairIcons';
import useTicker from '../../../hooks/useTicker';
import { switchCurrentTicker } from '../../../Authlogic';
import { formatTimestamp } from '../../../components/DexTrades/formatters';
import {
  getBigTradeBaseAmount,
  getBigTradeQuoteAmount,
  isBigTradeBuy,
} from '../BigTradesTable';
import TableCardMobile, {
  contentColors,
} from '../../../molecules/Table/mobile/TableCard';
import NumberDisplay from '../../../molecules/NumberDisplay';
import { formatLiquidity } from '../../../components/Modal/TickersModal/helpers';
import { formatWallet } from '../../../components/Modal/WalletModal/formatters';
import { Loader } from '../../../components/DexTrades';

const getColorForItem = (
  row: IDexBigTrade,
  accessor: string
): contentColors => {
  if (accessor === 'buy') {
    return isBigTradeBuy(row.buy, row.ticker) ? 'green' : 'red';
  }
  return 'grey';
};

interface IBigTradesTableMobileProps {
  data: IDexBigTrade[];
  inputSearch: string;
  loading: boolean;
  timezone?: string;
}

const BigTradesTableMobile = ({
  data,
  inputSearch,
  loading,
  timezone,
}: IBigTradesTableMobileProps) => {
  const [trades, setTrades] = useState<IDexBigTrade[]>(data);

  useEffect(() => {
    setTrades(
      data.filter(
        (trade) =>
          trade.ticker.name.toLowerCase().includes(inputSearch.toLowerCase()) ||
          trade.wallet.toLowerCase().includes(inputSearch.toLowerCase())
      )
    );
  }, [data, inputSearch]);

  const schema: IMobileTableScema = {
    row_1: {
      item_1: {
        name: 'Pair',
        accessor: 'ticker',
        parser: (value: ITicker) => {
          const ticker = useTicker(value);
          return (
            <div className='ticker__cell'>
              <PairIcons
                chain={ticker.chain}
                pair={ticker.name}
                base_address={ticker.base_address}
                quote_address={ticker.quote_address}
                base_slug={ticker.base_slug}
                quote_slug={ticker.quote_slug}
              />
              <Text size={14} className='text--weight-500 text--grey-400'>
                {ticker.name}
              </Text>
            </div>
          );
        },
        onClick: (row) => switchCurrentTicker(row.ticker),
      },
      item_2: {
        name: 'Price USD',
        accessor: 'price_usd',
        parser: (_, row) => (
          <NumberDisplay value={useTicker(row.ticker).price_usd} string />
        ),
        string: true,
      },
    },
    row_2: {
      item_1: {
        name: 'Time',
        accessor: 'timestamp',
        parser: (value) => formatTimestamp(value, timezone, 'full'),
      },
      item_2: {
        name: 'Type',
        accessor: 'buy',
        parser: (value, row) =>
          isBigTradeBuy(value, row.ticker) ? 'Buy' : 'Sell',
      },
    },
    row_3: {
      item_1: {
        name: 'Amount Quote',
        accessor: 'amount_quote',
        parser: (_, row) => (
          <NumberDisplay
            value={getBigTradeQuoteAmount(row, row.ticker)}
            round={2}
            string
          />
        ),
        string: true,
      },
      item_2: {
        name: 'Amount Base',
        accessor: 'amount_base',
        parser: (_, row) => (
          <NumberDisplay
            value={getBigTradeBaseAmount(row, row.ticker)}
            round={2}
            string
          />
        ),
        string: true,
      },
    },
    row_4: {
      item_1: {
        name: 'Value',
        accessor: 'value_usd',
        parser: (value) => formatLiquidity(value),
        string: true,
      },
      item_2: {
        name: 'Wallet',
        accessor: 'wallet',
        parser: (value, row) => (
          <Link
            to={`/en/wallet-overview/${row.chain}/${value}`}
            className='big_trades_mobile_wallet'
          >
            <div className='big_trades_mobile_wallet__icon icon-wallet' />
            <Text size={16} className='text--navy-600'>
              {formatWallet(value)}
            </Text>
          </Link>
        ),
      },
    },
  };

  return (
    <>
      {loading ? (
        <div className='centrify'>
          <Loader />
        </div>
      ) : !trades.length ? (
        <div className='centrify'>
          <Text size={14} className='text--grey-400'>
            No data
          </Text>
        </div>
      ) : (
        formatMobileTableData({
          data: trades,
          schema,
          getColorForItem,
          spaceBetween: true,
        }).map((row, index) => (
          <TableCardMobile key={index} data={{ ...row, opened: true }} />
        ))
      )}
    </>
  );
};

export default BigTradesTableMobile;
